import dayjs from "dayjs";
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Message, Segment, Select } from "semantic-ui-react";
import PropTypes from "prop-types";
import { t } from "../../constant/translations";

import { EditEntity } from "../../components/common/edit-entity";
import {
  clearError,
  createCharge,
  editCharge,
  deleteCharge,
} from "../../redux/modules/charge";

import {
  FormdataManager,
  getInitialState,
} from "../../components/common/formdata";
import { ErrorWrap } from "../../helpers/error";
import { withRefreshEstate } from "../../redux/modules/estates";
import { CalendarInput } from "../common/calendar-input";
import { Description } from "./charge-description";

function isSettled(entity) {
  return entity && entity.status === "settled";
}

export const ChargeEditComponent = ({
  estate,
  entity,
  trigger_props,
  ...otherProps
}) => {
  const [formData, setFormData] = useState(null);

  const funds = useSelector((state) => state.blocks.selected.funds);
  const loading = useSelector((state) => state.charge.pending);
  const error = useSelector((state) => ErrorWrap.fromError(state.charge.error));
  const dispatch = useDispatch();

  const handleCreate = (data) =>
    dispatch(withRefreshEstate(createCharge, estate.id)(data));
  const handleUpdate = (data) =>
    dispatch(withRefreshEstate(editCharge, estate.id)(data));
  const handleDelete = (data) =>
    dispatch(withRefreshEstate(deleteCharge, estate.id)(data));
  const handleClearError = () => dispatch(clearError());

  const initialState = getInitialState({
    estate: estate.id,
    charge_date: dayjs().format("YYYY-MM-DD"),
    ...entity,
  });

  const manager = new FormdataManager(
    formData || initialState,
    setFormData,
    initialState
  );

  const selectFundOptions = funds.map((p) => ({
    key: p.id,
    text: p.name,
    value: p.name,
  }));

  return (
    <EditEntity
      manager={manager}
      estate={estate}
      entity={entity}
      loading={loading}
      error={error}
      create={handleCreate}
      update={handleUpdate}
      del={handleDelete}
      clearError={handleClearError}
      {...otherProps}
      header={
        !entity
          ? t("labels.new_obligation") + ` ${estate.name}`
          : t("labels.obligation_change") + ` ${estate.name}`
      }
      trigger_props={trigger_props}
    >
      {!isSettled(entity) ? (
        <>
          <Form.Field>
            <label>{t("titles.date_of_obligation")}</label>
            <CalendarInput {...manager.getAttributes("charge_date")} />
            {error.has("charge_date") && (
              <span>{error.get("charge_date")}</span>
            )}
          </Form.Field>
          <Form.Field>
            <label>{t("labels.direction")}</label>
            <Select
              placeholder={t("labels.select_direction")}
              options={selectFundOptions}
              {...manager.getAttributes("fund")}
            />
            {error.has("fund") && <span>{error.get("fund")}</span>}
          </Form.Field>
          <Form.Field>
            <label>{t("labels.amount")}</label>
            <Form.Input
              placeholder={t("labels.amount")}
              error={error.has("amount")}
              {...manager.getAttributes("amount")}
            />
            {error.has("amount") && <span>{error.get("amount")}</span>}
          </Form.Field>
          <Form.Field>
            <Form.TextArea
              label={t("labels.description")}
              {...manager.getAttributes("description")}
            />
            {error.has("description") && (
              <span>{error.get("description")}</span>
            )}
          </Form.Field>
        </>
      ) : (
        <Message>{t("info.obligation_paid")}</Message>
      )}
      {entity && entity.details.length !== 0 ? (
        <Segment>
          {!isSettled(entity) ? (
            <Message>
              {t("info.obligation_generated1")}
              <br /> {t("info.obligation_generated2")}
            </Message>
          ) : null}
          <Description charge={entity} />
        </Segment>
      ) : null}
    </EditEntity>
  );
};

ChargeEditComponent.propTypes = {
  estate: PropTypes.object.isRequired,
  entity: PropTypes.object,
  trigger_props: PropTypes.object,
};

export const ChargeEdit = ChargeEditComponent;
